import { DateTime } from "luxon";
import { TeamScheduleRow } from "./groupTeamSchedule.js";

// const USER_TIMEZONE = "America/Los_Angeles"; // User's local timezone

interface Task {
  original: TeamScheduleRow["dates"][number];
  start: number;
  end: number;
  idx: number;
  row: number;
  label: string;
}

/**
 * For each group of dates, assign `.row` so non‑overlapping items share a row.
 *
 * @param dates     – the full list of date‑records for one person
 * @param viewStart – JS Date at 00:00 of your leftmost column
 * @param viewEnd   – JS Date at 00:00 of one past your rightmost column
 *
 * Side‑effects: each dates[i].row is set to 0,1,2…
 * Returns the same `dates` array, mutated in place.
 */
function assignRowsToDates(dates: TeamScheduleRow["dates"], timeZone: string) {
  const tasks = dates.map((d, idx) => ({
    original: d,
    start: new Date(d.start_time).valueOf(),
    end: new Date(d.end_time).valueOf(),
    idx,
    row: -1,
    label: d.label
  }));

  // 2) Sort by start ↑, then by end ↑ (so back‑to‑back events go in the same row)
  tasks.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return a.end - b.end;
  });

  // 3) Greedy row assignment based on UTC non-overlap AND local day separation
  const rowsData: Array<{
    lastTaskEndUtcMs: number;
    lastTaskLocalEndDayOrdinal: number;
  }> = [];

  for (const t of tasks) {
    // tasks are { original, start (utcMs), end (utcMs), idx, row, label }
    const t_utc_start_ms = t.start;
    const t_utc_end_ms = t.end;
    const t_local_start_dt =
      DateTime.fromMillis(t_utc_start_ms).setZone(timeZone);
    // Using year and ordinal to ensure uniqueness across year boundaries
    const t_local_start_year = t_local_start_dt.year;
    const t_local_start_day_ordinal = t_local_start_dt.ordinal;

    let assignedRow = -1;

    for (let r = 0; r < rowsData.length; r++) {
      const rowInfo = rowsData[r];

      // Condition 1: No direct UTC overlap
      const noUtcOverlap = rowInfo.lastTaskEndUtcMs <= t_utc_start_ms;

      if (!noUtcOverlap) {
        continue; // Cannot place if UTC overlaps
      }

      // If UTC is fine, check local day separation.
      // For an empty row (initial state), localDayCondition is met, allowing the first task.
      if (rowInfo.lastTaskEndUtcMs === 0) {
        assignedRow = r;
        break;
      }

      // Condition 2: Local day separation logic
      const prev_local_end_dt = DateTime.fromMillis(
        rowInfo.lastTaskEndUtcMs
      ).setZone(timeZone);

      let effective_prev_end_year = prev_local_end_dt.year;
      let effective_prev_end_ordinal = prev_local_end_dt.ordinal;

      // If the previous task on this row ended exactly at midnight local time,
      // and it's not the epoch (initial value for lastTaskEndUtcMs),
      // then for comparison purposes, treat it as ending on the previous calendar day.
      if (
        prev_local_end_dt.hour === 0 &&
        prev_local_end_dt.minute === 0 &&
        prev_local_end_dt.second === 0 &&
        prev_local_end_dt.millisecond === 0 &&
        rowInfo.lastTaskEndUtcMs > 0 // Ensure it's a real task end time, not initial 0
      ) {
        const dayBefore = prev_local_end_dt.minus({ days: 1 });
        effective_prev_end_year = dayBefore.year;
        effective_prev_end_ordinal = dayBefore.ordinal;
      }

      let localDayConditionMet = false;
      if (t_local_start_year > effective_prev_end_year) {
        localDayConditionMet = true;
      } else if (t_local_start_year === effective_prev_end_year) {
        localDayConditionMet =
          t_local_start_day_ordinal > effective_prev_end_ordinal;
      }

      if (localDayConditionMet) {
        // noUtcOverlap is already true here
        assignedRow = r;
        break;
      }
    }

    if (assignedRow === -1) {
      // If no existing row can accommodate the task, assign to a new row
      assignedRow = rowsData.length;
      // Initialize with values that will allow the first task to be placed
      rowsData.push({ lastTaskEndUtcMs: 0, lastTaskLocalEndDayOrdinal: 0 });
    }

    t.row = assignedRow;

    // Update row state
    const t_local_end_dt = DateTime.fromMillis(t_utc_end_ms).setZone(timeZone);
    rowsData[assignedRow].lastTaskEndUtcMs = t_utc_end_ms;
    // Store the ordinal of the day the task ENDS ON in the local timezone.
    rowsData[assignedRow].lastTaskLocalEndDayOrdinal = t_local_end_dt.ordinal;
  }

  // Post-processing: Reorder rows by density (total duration)
  const numRows = rowsData.length;
  if (numRows > 1) {
    const rowsContent: Task[][] = Array(numRows)
      .fill(null)
      .map(() => []);
    for (const t of tasks) {
      if (t.row !== undefined && t.row >= 0 && t.row < numRows) {
        rowsContent[t.row].push(t);
      }
    }

    const rowDensities: number[] = rowsContent.map((rowTasks) =>
      rowTasks.reduce((sum, task) => sum + (task.end - task.start), 0)
    );

    // Create an array of original row indices to sort: [0, 1, ..., numRows-1]
    const sortedRowIndices = Array.from({ length: numRows }, (_, i) => i);

    // Sort original row indices by their density, descending.
    // If densities are equal, maintain original relative order (stable sort aspect).
    sortedRowIndices.sort((a, b) => {
      const densityDiff = rowDensities[b] - rowDensities[a];
      if (densityDiff !== 0) return densityDiff;
      return a - b; // Stable sort: preserve original order for ties
    });

    const oldToNewRowMap: number[] = Array(numRows);
    for (let newRowIdx = 0; newRowIdx < numRows; newRowIdx++) {
      const oldRowIdx = sortedRowIndices[newRowIdx];
      oldToNewRowMap[oldRowIdx] = newRowIdx;
    }

    for (const t of tasks) {
      if (t.row !== undefined && t.row >= 0 && t.row < numRows) {
        t.row = oldToNewRowMap[t.row];
      }
    }
  }

  // 4) Re‑assemble in original order, carrying over `row`
  const output: (TeamScheduleRow["dates"][number] & { row: number })[] = Array(
    dates.length
  );
  for (const t of tasks) {
    output[t.idx] = {
      ...t.original,
      row: t.row
    };
  }

  // 5) OPTIONAL: if you want to render in strict row‑major order, uncomment this
  // output.sort((a, b) => {
  //   if (a.row !== b.row) return a.row - b.row;
  //   return new Date(a.start_time).valueOf() - new Date(b.start_time).valueOf();
  // });
  return output;
}

export const densePacking = (input: TeamScheduleRow[], timeZone: string) => {
  return input.map((group) => ({
    ...group,
    dates: assignRowsToDates(group.dates, timeZone)
  }));
};
